import { Purchases } from '@revenuecat/purchases-capacitor';
import { useContext } from 'react';
import { useHistory } from "react-router";
import { useAppState } from '../AppListenerProvider';
import { AppContext } from '../AppStateProvider';
import { useAuth } from '../AuthProvider';
import { useSignUp } from "../components/ComponentsOnboarding/SignUpProvider/SignUpProvider";
import { OnboardingStatus } from "../models/onboardingStatus";
import { UserType } from '../models/usertype';
import PreferencesService from '../services/preferences.service';
import UserService from '../services/user.service';
import { usePushNotifications } from './usePushNotifications';

interface AuthServiceHook {
    login: () => Promise<void>;
    setAuthVariables: () => Promise<UserType>;
}

const useAuthService = (): AuthServiceHook => {
    const history = useHistory();
    const { setStep } = useSignUp();
    const { user } = useAuth();
    const { dispatch } = useContext(AppContext);
    const { checkPushNotificationPermission } = usePushNotifications();
    const { isNative } = useAppState();

    const logInToRevenueCat = async (retrievedUserId: string) => {
        console.log("Logging Into Revenue cat with User ID: ", retrievedUserId);
        await Purchases.logIn({ appUserID: retrievedUserId });
    }

    // AUTH LOGIC
    const setAuthVariables = async () => {
        await PreferencesService.setUserUid(user?.uid);
        await UserService.checkCreateUser(user.uid, user.email, user.displayName);
        await UserService.setUser(true, dispatch);
        const retrievedUserType = await UserService.getUserType(user?.uid)
        console.log("Entering isAuthenticated for Payments");

        if (isNative && retrievedUserType === UserType.USER) {
            console.log("Entering Authenticated for Payments Natively to set up Revenue Cat");
            const retrievedUserFromAuthId = await UserService.getUserByAuthId(user?.uid);
            if (retrievedUserFromAuthId) {
                await logInToRevenueCat(retrievedUserFromAuthId.id);
            }
        }

        // Phone verification is owned by PhoneVerificationProvider — it determines the gate
        // reactively from auth state (server phone status + a user-scoped marker), so it engages
        // immediately on login/sign-up rather than depending on this function completing.

        await checkPushNotificationPermission();

        return retrievedUserType;
    }

    // Auth is directly tied to sign up / onboarding which is why we have the sign up in this hook
    const login = async (): Promise<void> => {
        await setStep(OnboardingStatus.INIT);
        history.push('/');
    };

    return {
        login,
        setAuthVariables
    };
}

export default useAuthService;